import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getData, getcountries, getseries, getsubdomains } from '../../../services/finance/revenueStatus/revenueServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Revenue = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeoutRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({
  countries: [],
  subdomains: [],
  series: []
  });
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

// Debounce Effect
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1); // reset page only when actual search triggered
    }, 600); // 500ms debounce delay

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchText]);

  // Fetch data list
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
          const filters = {};
          if (seriesFilter) filters.series = seriesFilter;
          if (subDomainFilter) filters.sub_domain = subDomainFilter;
          if (countryFilter) filters.country = countryFilter;
    
          const params = {
            pdf_status: 1,
            page: currentPage,
            limit: rowsPerPage,
            search: debouncedSearch,
            filters,
            startDate: startDate || null,
            endDate: endDate || null,
          };
    
          const response = await getData(params);
          // ✅ Console log the full API response
          console.log('Fetched Data:', response);
          setDataList(response.rows);
          setTotalRows(response.count);
        } catch (error) {
          console.error('Failed to fetch data:', error);
        } finally {
          setLoading(false);
        }
    }, [currentPage, rowsPerPage, debouncedSearch, subDomainFilter, seriesFilter, countryFilter, startDate, endDate]);
        
    // Fetch all dropdown data
    const fetchDropdownData = useCallback(async () => {
      try {
        const [country, subdomains, series] = await Promise.all([
          getcountries(),
          getsubdomains(),            
          getseries()                        
        ]);
        const sortedSubdomains = subdomains.sort((a, b) =>
        (a.sub_domain || "").localeCompare(b.sub_domain || "")
      );
          // ✅ Make series unique based on string value
        // const uniqueSeries = [...new Set(series.map(item => item.series))].map(series => ({
        //   series: series
        // }));
        const uniqueSeries = [...new Set(series.map(item => item.series))]
      .sort((a, b) => b.localeCompare(a))// Sort descending
      .map(series => ({
        series
      }));

    
        setDropdownData({
          countries: country,
          subdomains: sortedSubdomains,
          series: uniqueSeries
        });
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    }, []);


  useEffect(() => {
      fetchDropdownData();
    }, [fetchDropdownData]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);
  

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };
  
  const getCurrencySymbol = (currencyCode) => {
  switch (currencyCode?.toUpperCase()) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'INR': return '₹';
    case 'GBP': return '£';
    case 'RUB': return '₽';
    case 'THB': return '฿';
    default: return currencyCode || '';
  }
};

  //based country code append country name
    const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const exportToExcel = () => {
      const filtered = dataList.map((row) => {
        let eventVenue = '';

        if (row.event_date) {
          const parts = row.event_date.split('|');
          if (parts.length === 2) {
            eventVenue = parts[1].trim(); // Get venue
          }
        }

        return {
          'Series': row.series,
          'Sub Domain': row.sub_domain,
          'Date of Payment': formatDate(row.date_of_payment), // Correct date format
          'Event Venue': eventVenue,                          // Extracted venue
          'Name': row.billing_full_name,
          'Country': getCountryName(row.billing_countries),
          'Amount': row.reg_amount,
          'Event': row.event_type,
          'Currency': row.currency_type,
          'Payment': row.payment_type,
          'Billing Address': row.billing_address,
        };
      });

      const ws = XLSX.utils.json_to_sheet(filtered);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Revenue');
      XLSX.writeFile(wb, `Revenue_${startDate || 'all'}_to_${endDate || 'all'}.xlsx`);
    };


    const exportToPDF = () => {
    const doc = new jsPDF();

    const tableData = dataList.map((row, index) => {
      let eventVenue = '';
      if (row.event_date) {
        const parts = row.event_date.split('|');
        if (parts.length === 2) {
          eventVenue = parts[1].trim(); // Extract venue
        }
      }

      return [
        index + 1,
        row.series,
        row.sub_domain,
        formatDate(row.date_of_payment), // Correct date format
        eventVenue,                      // Extracted venue
        row.billing_full_name,
        getCountryName(row.billing_countries),
        row.reg_amount,
        row.event_type,
        row.currency_type,
        row.payment_type,
        row.billing_address,
      ];
    });

  doc.autoTable({
    head: [
      [
        '#',
        'Series',
        'Sub Domain',
        'Date of Payment',
        'Event Venue',
        'Name',
        'Country',
        'Amount',
        'Event Type',
        'Currency',
        'Payment',
        'Billing Address',
      ],
    ],
    body: tableData,
  });

  doc.save(`Revenue_${startDate || 'all'}_to_${endDate || 'all'}.pdf`);
};


  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      width: '60px',
    },
    { name: 'Date Of Payment', selector: (row) => row.date_of_payment, sortable: true ,width:'100px', wrap:true},
{
    name: 'Series',
    selector: (row) => {
      // Assuming the `series` field is in format like "2025-11" or "2025-11-15"
      const date = new Date(row.series);
      // Format as "2025-11 Nov 2025"
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-based index, so we add 1
      const months = [

        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'

      ];
      return ` ${months[month]} ${year}`;
    },
    sortable: true,
    wrap:true
   },    
   { name: 'Sub Domain', selector: (row) => row.sub_domain, sortable: true ,width:'130px',wrap:true},
    { name: 'Name', selector: (row) => row.billing_full_name, sortable: true , wrap:true},
    { name: 'Country', selector: (row) =>  getCountryName(row.billing_countries), sortable: true, wrap:true },
    {
      name: 'Reg Amount',
      selector: (row) => {
        const currencySymbol = getCurrencySymbol(row.currency_type);
        return <b className='fs-4'>{currencySymbol} {row.reg_amount}</b>;
      },
      sortable: true,
    },
    { name: 'Event Type', selector: (row) => row.event_type, sortable: true },
    { name: 'Currency Type', selector: (row) => row.currency_type, sortable: true, width:'100px',wrap:true },
    { name: 'Payment Type', selector: (row) => row.payment_type, sortable: true ,width:'100px', wrap:true},
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        <div className=" row m-3 d-flex flex-column gap-3 ">
          <div className="d-flex gap-2">
            {/* Dropdown for Filters */}
            <div className="col-md-2">
              <span className='fs-5 text-dark'>Series</span>
              <select className="form-control" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
                <option value="">All Series</option>
                {dropdownData.series.map((series) => (
                  <option key={series.series} value={series.series}>{series.series}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span className='fs-5 text-dark'>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
                <option value="">All Subdomains</option>
                {dropdownData.subdomains.map((subdomains) => (
                  <option key={subdomains.sub_domain} value={subdomains.sub_domain}>{subdomains.sub_domain}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
            <span className='fs-5 text-dark'>Countries</span>
            <select
              className="form-control"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <option value="">All Countries</option>
              {dropdownData.countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

            {/* Start Date and End Date Input */}
            <div className="col-md-2">
              <span className='fs-5 text-dark'>Start Date</span>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <span className='fs-5 text-dark'>End Date</span>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportToExcel}>Export Excel</button>
            <button className="btn btn-danger" onClick={exportToPDF}>Export PDF</button>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div> 
            <div className="container-fluid p-4">
              <div className="row mb-3">
                <div className="col">
                  <p className="fs-4 text-dark">Revenue Status / Revenue</p>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={dataList}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[50, 100, 500]}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                responsive
                striped
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
            />
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Revenue;
